/**
 * Little Worlds Toy Co.
 * Wishlist Hero → Horizon cart synchronisation
 *
 * Updates the header cart count and cart drawer after
 * Wishlist Hero successfully adds products to Shopify.
 *
 * Does not add products, open the drawer or navigate away.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const nativeOpen = XMLHttpRequest.prototype.open;
const nativeSend = XMLHttpRequest.prototype.send;

const requests = new WeakMap();

let refreshTimer;
let generation = 0;

function isCartEndpoint(url, name) {
  try {
    const parsed = new URL(String(url), location.href);

    return (
      parsed.origin === location.origin &&
      new RegExp(`/cart/${name}\\.js$`).test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

async function refreshHorizonCart() {
  const version = ++generation;

  try {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Cart refresh failed (${response.status})`);
    }

    const ajaxCart = await response.json();

    if (version !== generation) return;

    const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(
      ajaxCart
    );

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'standard-action',

        lines: (ajaxCart.items || []).map((item) => ({
          id: String(item.key),
          quantity: item.quantity,
        })),

        promise: Promise.resolve({
          cart,
          detail: {
            source: 'lw-wishlist-cart-sync',
            items: ajaxCart.items,
          },
        }),
      })
    );
  } catch (error) {
    console.warn(
      '[Little Worlds] Wishlist cart synchronisation:',
      error
    );
  }
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);

  refreshTimer = setTimeout(refreshHorizonCart, 500);
}

/**
 * Record XMLHttpRequest destinations.
 */
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  requests.set(this, {
    method: String(method).toUpperCase(),
    url: String(url),
  });

  return nativeOpen.call(this, method, url, ...rest);
};

/**
 * Detect successful Wishlist Hero cart additions.
 */
XMLHttpRequest.prototype.send = function (...args) {
  const request = requests.get(this);

  if (
    request?.method === 'POST' &&
    isCartEndpoint(request.url, 'add')
  ) {
    const initiator = new Error().stack || '';

    // Wishlist Hero's observed JavaScript bundle.
    if (/bundle2(?:\.[\w-]+)?\.js/.test(initiator)) {
      this.addEventListener(
        'loadend',
        () => {
          if (this.status >= 200 && this.status < 300) {
            scheduleRefresh();
          }
        },
        { once: true }
      );
    }
  }

  return nativeSend.apply(this, args);
};